import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Upload, Plus, Loader2 } from 'lucide-react';
import api from "../../../../api/axios";
import { useNavigate } from 'react-router-dom';

const AddProduct = () => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]); // Holds children of selected category
  const [selectedParentId, setSelectedParentId] = useState(''); // Tracking parent separately
  const [mainPreview, setMainPreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '', brand: '', price: '', old_price: '',
    description: '', stock: 10, category_id: '',
    main_image: null, gallery: [],
    promos: false
  });

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data.data));
  }, []);

  // Handle Parent Category Change
  const handleParentCategoryChange = (e) => {
    const parentId = e.target.value;
    setSelectedParentId(parentId);
    
    // Find the selected category object to get its children
    const selectedCat = categories.find(c => String(c.id) === String(parentId));
    
    if (selectedCat && selectedCat.children) {
      setSubCategories(selectedCat.children);
    } else {
      setSubCategories([]);
    }

    // Reset sub-category and set category_id to parent initially
    setFormData({ ...formData, category_id: parentId });
  };

  const handleMainImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, main_image: file });
      setMainPreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryImages = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({ ...prev, gallery: [...prev.gallery, ...files] }));
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setGalleryPreviews(prev => [...prev, ...newPreviews]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Final Validation: Ensure we use the Sub-category ID if it exists
    // The formData.category_id is already being updated by the onChange handlers

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'gallery') {
        formData.gallery.forEach((file) => data.append('gallery[]', file));
      }
      else if (key === 'promos') {
        data.append('promos', formData.promos ? 1 : 0);
      }
      else {
        data.append(key, formData[key]);
      }
    });

    try {
      await api.post('/products', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert("Produit publié avec succès !");
      navigate("../products");
    } catch (err) {
      alert("Erreur lors de la publication");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-6xl mx-auto p-6">
      <form onSubmit={handleSubmit} className="bg-white rounded-[2.5rem] shadow-2xl p-8 border border-gray-100 space-y-8">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: General Info */}
          <div className="space-y-6">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-[#ffaa00]">Détails du Produit</h3>

            <input required placeholder="Nom du produit" className="w-full p-4 bg-gray-50 rounded-2xl outline-none font-bold"
              onChange={e => setFormData({ ...formData, title: e.target.value })} />

            <div className="grid grid-cols-1 gap-4">
              <input required placeholder="Marque (Brand)" className="w-full p-4 bg-gray-50 rounded-2xl outline-none font-bold"
                onChange={e => setFormData({ ...formData, brand: e.target.value })} />
              
              <div className="grid grid-cols-2 gap-4">
                {/* Parent Category Select */}
                <div className="space-y-1">
                    <label className="text-[9px] font-black uppercase ml-2 text-gray-400">Catégorie Parente</label>
                    <select required className="w-full p-4 bg-gray-50 rounded-2xl outline-none font-bold text-sm"
                        value={selectedParentId}
                        onChange={handleParentCategoryChange}>
                        <option value="">Sélectionner...</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>

                {/* Sub Category Select - Only shows if children exist */}
                <div className="space-y-1">
                    <label className="text-[9px] font-black uppercase ml-2 text-gray-400">Sous-Catégorie</label>
                    <select 
                        disabled={subCategories.length === 0}
                        required={subCategories.length > 0}
                        className="w-full p-4 bg-gray-50 rounded-2xl outline-none font-bold text-sm disabled:opacity-50"
                        onChange={e => setFormData({ ...formData, category_id: e.target.value })}>
                        <option value="">{subCategories.length > 0 ? "Choisir sous-catégorie" : "N/A"}</option>
                        {subCategories.map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                    </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <input required type="number" placeholder="Prix" className="p-4 bg-gray-50 rounded-2xl outline-none font-bold border-2 border-[#ffaa00]/20"
                onChange={e => setFormData({ ...formData, price: e.target.value })} />
              <input type="number" placeholder="Ancien Prix" className="p-4 bg-gray-50 rounded-2xl outline-none font-bold text-gray-400"
                onChange={e => setFormData({ ...formData, old_price: e.target.value })} />
              <input required type="number" placeholder="Stock" className="p-4 bg-gray-50 rounded-2xl outline-none font-bold"
                onChange={e => setFormData({ ...formData, stock: e.target.value })} />
            </div>

            <textarea placeholder="Description du produit..." rows="4" className="w-full p-4 bg-gray-50 rounded-2xl outline-none font-bold"
              onChange={e => setFormData({ ...formData, description: e.target.value })} />
          </div>

          {/* Right: Media */}
          <div className="space-y-6">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">Image Principale (Main)</h3>
            <label className="block h-64 border-2 border-dashed border-gray-100 rounded-[2rem] relative overflow-hidden cursor-pointer group hover:border-[#ffaa00] transition-all bg-gray-50/50">
              {mainPreview ? (
                <img src={mainPreview} className="w-full h-full object-contain" alt="Main" />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <Upload size={30} />
                  <span className="text-[10px] font-black uppercase mt-2">Upload Image</span>
                </div>
              )}
              <input required type="file" className="hidden" onChange={handleMainImage} />
            </label>

            <h3 className="text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">Galerie Secondaire</h3>
            <div className="grid grid-cols-4 gap-3">
              <label className="h-20 border-2 border-dashed border-gray-100 rounded-xl flex items-center justify-center cursor-pointer hover:bg-orange-50 transition-all text-gray-300">
                <Plus size={20} />
                <input type="file" multiple className="hidden" onChange={handleGalleryImages} />
              </label>
              {galleryPreviews.map((src, i) => (
                <div key={i} className="relative h-20 rounded-xl overflow-hidden shadow-sm">
                  <img src={src} className="w-full h-full object-cover" alt="Gallery" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 bg-orange-50/50 rounded-2xl border border-orange-100">
          <input
            type="checkbox"
            id="promos"
            className="w-5 h-5 accent-[#ffaa00] cursor-pointer"
            checked={formData.promos}
            onChange={e => setFormData({ ...formData, promos: e.target.checked })}
          />
          <label htmlFor="promos" className="text-xs font-black uppercase tracking-widest text-orange-600 cursor-pointer select-none">
            Ajouter ce produit à la page Promotions
          </label>
        </div>

        <button disabled={loading} className="w-full py-5 bg-[#ffaa00] text-white rounded-2xl font-black uppercase tracking-[0.2em] shadow-xl hover:bg-black transition-all flex items-center justify-center gap-2">
          {loading ? <Loader2 className="animate-spin" /> : "Créer le Produit"}
        </button>
      </form>
    </motion.div>
  );
};

export default AddProduct;